import "../../css/blocks/faq.css";

/**
 * FAQ accordion.
 *
 * The markup is native <details>/<summary> and works without this script; it
 * only adds the height transition and the fragment deep link.
 */

const DURATION = 250;
const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

const animations = new WeakMap<HTMLElement, Animation>();
const targetStates = new WeakMap<HTMLDetailsElement, boolean>();

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Toggle an item by animating its content wrapper's height.
 *
 * While closing, `details.open` stays true until the animation finishes, so the
 * intended state is tracked separately.
 */
function toggleItem(details: HTMLDetailsElement, content: HTMLElement): void {
  const shouldOpen = !(targetStates.get(details) ?? details.open);

  // Read before cancelling so an interrupted animation resumes from the
  // height currently on screen.
  const startHeight = details.open ? content.offsetHeight : 0;
  animations.get(content)?.cancel();

  targetStates.set(details, shouldOpen);

  if (shouldOpen) {
    details.open = true;
  }

  const endHeight = shouldOpen ? content.scrollHeight : 0;
  content.style.overflow = "hidden";

  const animation = content.animate(
    { height: [`${startHeight}px`, `${endHeight}px`] },
    { duration: DURATION, easing: EASING },
  );

  animations.set(content, animation);

  animation.addEventListener("finish", () => {
    animations.delete(content);
    targetStates.delete(details);
    content.style.removeProperty("overflow");

    if (!shouldOpen) {
      details.open = false;
    }
  });
}

/**
 * Drop an item's animation state so a running transition cannot undo a direct
 * change to `open`.
 */
function clearAnimation(details: HTMLDetailsElement): void {
  const content = details.querySelector<HTMLElement>(".faq__content");

  if (content) {
    animations.get(content)?.cancel();
    animations.delete(content);
    content.style.removeProperty("overflow");
  }

  targetStates.delete(details);
}

/**
 * Open the question the current URL fragment refers to.
 */
function openQuestionFromHash(): void {
  const id = window.location.hash.slice(1);

  if (!id) {
    return;
  }

  const details = document.getElementById(id);

  if (!(details instanceof HTMLDetailsElement) || !details.classList.contains("faq__item")) {
    return;
  }

  clearAnimation(details);
  details.open = true;
  details.scrollIntoView({ block: "start" });
}

// Delegated so blocks re-rendered by the editor stay functional.
document.addEventListener("click", (event) => {
  const target = event.target as HTMLElement | null;
  const summary = target?.closest(".faq__item > summary");

  if (!(summary instanceof HTMLElement)) {
    return;
  }

  const details = summary.parentElement as HTMLDetailsElement;
  const content = details.querySelector<HTMLElement>(".faq__content");

  if (!content || prefersReducedMotion()) {
    return;
  }

  event.preventDefault();
  toggleItem(details, content);
});

document.addEventListener("DOMContentLoaded", openQuestionFromHash);
window.addEventListener("hashchange", openQuestionFromHash);
